
import React, { useState } from "react";
import { SwapRequest } from "@/services/myReagents";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import SwapResponseDialog from "./SwapResponseDialog";
import EditSwapRequestDialog from "./EditSwapRequestDialog";
import DeleteSwapRequestDialog from "./DeleteSwapRequestDialog";
import { useSwapRequestsManager } from "@/hooks/useSwapRequestsManager";
import { useAuth } from "@/contexts/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SwapRequestsTabContent from "./SwapRequestsTabContent";

interface SwapRequestsTableProps {
  swapRequests: SwapRequest[];
  isLoading: boolean;
  onSwapRequestUpdated: () => void;
}

const SwapRequestsTable: React.FC<SwapRequestsTableProps> = ({ 
  swapRequests, 
  isLoading, 
  onSwapRequestUpdated 
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"received" | "sent">("received");
  const userId = user?.id;
  
  const {
    selectedRequest,
    action,
    message,
    isSubmitting,
    setMessage,
    handleApprove,
    handleReject,
    handleClose,
    handleUpdateStatus,
    handleEdit,
    handleDelete,
    handleSaveEdit,
    handleConfirmDelete,
    isEditDialogOpen,
    isDeleteDialogOpen,
    closeEditDialog,
    closeDeleteDialog
  } = useSwapRequestsManager(onSwapRequestUpdated);
  
  // Log all swap requests to verify we're receiving all expected data
  console.log("SwapRequestsTable received:", swapRequests.length, "requests");
  
  // Separate requests into sent and received
  const sentRequests = swapRequests.filter(req => req.requester_id === userId);
  const receivedRequests = swapRequests.filter(req => req.reagents.user_id === userId);
  
  console.log("Sent requests:", sentRequests.length);
  console.log("Received requests:", receivedRequests.length);
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }
  
  if (swapRequests.length === 0) {
    return (
      <SwapRequestsTabContent
        requests={[]}
        isSentByUser={false}
      />
    );
  }
  
  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "received" | "sent")}>
        <TabsList className="mb-4">
          <TabsTrigger value="received">
            Received Requests ({receivedRequests.length})
          </TabsTrigger>
          <TabsTrigger value="sent">
            Sent Requests ({sentRequests.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="received">
          <SwapRequestsTabContent
            requests={receivedRequests}
            isSentByUser={false}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        </TabsContent>
        
        <TabsContent value="sent">
          <SwapRequestsTabContent
            requests={sentRequests}
            isSentByUser={true}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </TabsContent>
      </Tabs>
      
      <SwapResponseDialog
        selectedRequest={selectedRequest}
        action={action}
        message={message}
        isSubmitting={isSubmitting}
        onMessageChange={setMessage}
        onSubmit={handleUpdateStatus}
        onClose={handleClose}
      />

      <EditSwapRequestDialog
        isOpen={isEditDialogOpen}
        onClose={closeEditDialog}
        onSave={handleSaveEdit}
        isSubmitting={isSubmitting}
        swapRequest={selectedRequest}
      />

      <DeleteSwapRequestDialog
        isOpen={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default SwapRequestsTable;
